import { MenuAreas, MenuItemvalidation } from './Sidebar.interface';

import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import { DynamicConfigs, FeatureGates } from 'modules/common/constants/featuregates';
import CalculatorIcon from 'modules/common/components/Icons/CalculatorIcon';
import CalendarCoinIcon from 'modules/common/components/Icons/CalendarCoinIcon';
import CheckCircleIcon from 'modules/common/components/Icons/CheckCircleIcon';
import GiftIconThin from 'modules/common/components/Icons/GiftIconThin';
import HomeIcon from 'modules/common/components/Icons/HomeIcon';
import InvoiceIcon from 'modules/common/components/Icons/InvoiceIcon';
import NavigationDocumentsIcon from 'modules/common/components/Icons/NavigationDocumentsIcon';
import ShieldCheckIcon from 'modules/common/components/Icons/ShieldCheckIcon';
import TimeCoinsIcon from 'modules/common/components/Icons/TimeCoinsIcon';
import {
    HEALTH_INSURANCE_UNLOCK_TASKS,
    getHealthInsuranceLockedTooltip,
} from 'modules/Insurance/HealthInsurance/healthInsuranceAccess';
import { Member } from 'modules/common/interfaces/member';
import { MembershipTier } from 'modules/common/types';
import { hasPersonalAccounts } from 'modules/Onboarding/AccountingSetup';
import { isLLCMember, isSCorpMember } from 'utils/businessStructure';
import { isInOnboardingStages } from 'modules/common/utils/memberStage';
import { OnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';

const accountingItemValidation: MenuItemvalidation = (member, oauthData) => {
    if (!oauthData) {
        return false;
    }
    return oauthData.connections?.hurdlr?.connected || isInOnboardingStages(member?.stage);
};

const isAccountingSetupComplete = (member: Member, onboardingProgress: OnboardingProgress): boolean => {
    const isTransactionImportComplete =
        onboardingProgress.transaction_import_status === 'completed' ||
        onboardingProgress.transaction_import_status === 'skipped' ||
        !hasPersonalAccounts(onboardingProgress);

    const isHomeOfficeComplete =
        onboardingProgress.home_office_setup_status === 'completed' ||
        onboardingProgress.home_office_setup_status === 'skipped' ||
        !isSCorpMember(member?.usertype || '');

    const isUploadDocumentsComplete = onboardingProgress.other_document_upload_status === 'completed';

    return isTransactionImportComplete && isHomeOfficeComplete && isUploadDocumentsComplete;
};

const accountingSubItemValidation: MenuItemvalidation = (member, _, onboardingProgress) => {
    // Only onboarding members need to complete accounting setup before accessing these items
    if (!isInOnboardingStages(member?.stage)) {
        return true;
    }
    return !!onboardingProgress && isAccountingSetupComplete(member, onboardingProgress);
};

const businessTaxReturnValidation: MenuItemvalidation = (member) => {
    if (!member?.usertype) {
        return false;
    }
    // Hide Business Tax Return for LLC members
    return !isLLCMember(member.usertype);
};

// Hide Payroll menu for LLC tier (free tier)
const payrollItemValidation: MenuItemvalidation = (member) => {
    // Show payroll only for SCorp (core) members
    return member?.membership_tier === MembershipTier.core;
};

// Mirrors the Payroll page's own check (`SelfServiceTab`), which shows the setup screen until
// `gep_eligible` is true. Until then these pages have nothing to show, so they're hidden, the area
// collapses to a plain `/payroll` link, and clicking Payroll lands on setup.
const payrollSetupCompleteValidation: MenuItemvalidation = (...[, , , payrollService]) =>
    payrollService?.gep_eligible === true;

export const menuAreas: MenuAreas = [
    {
        name: 'Home',
        icon: HomeIcon,
        path: '/dashboard',
    },
    {
        name: 'Tasks',
        icon: CheckCircleIcon,
        path: '/tasks',
        featureGate: {
            name: FeatureGates.MemberTasksUiEnabled,
            value: true,
        },
    },
    {
        name: 'Accounting',
        icon: CalculatorIcon,
        basePath: '/bookkeeping',
        customValidation: accountingItemValidation,
        requiredTask: ['open_connect_business_bank_account', 's_corp_election'],
        submenu: [
            {
                name: 'Overview',
                path: '/bookkeeping/dashboard',
            },
            {
                name: 'Transactions',
                path: '/bookkeeping/transactions',
                customValidation: accountingSubItemValidation,
            },
            {
                name: 'Reimbursements',
                path: '/bookkeeping/reimbursement-form',
                customValidation: accountingSubItemValidation,
            },
            {
                name: 'Reports',
                path: '/bookkeeping/reports',
            },
            {
                name: 'General ledger',
                path: '/bookkeeping/general-ledger',
            },
        ],
    },
    {
        name: 'Reimbursements',
        path: '/bookkeeping/reimbursement-form',
        icon: CalculatorIcon,
        customValidation: (_, oauthData) => {
            if (!oauthData) {
                return false;
            }
            return !accountingItemValidation(_, oauthData);
        },
    },
    {
        name: 'Invoicing',
        icon: InvoiceIcon,
        path: '/bookkeeping/invoicing',
        customValidation: accountingItemValidation,
    },
    {
        name: 'Payroll',
        icon: CalendarCoinIcon,
        basePath: '/payroll',
        requiredTask: ['open_connect_business_bank_account', 's_corp_election'],
        customValidation: payrollItemValidation,
        // The revamped pages are gated, and they are the only reason this area has a submenu at
        // all. With the gate off the submenu filters down to the dashboard alone and
        // `useMenuAreas` collapses the area to a plain link to it.
        // Overview first on purpose: clicking Payroll opens the first submenu item (Item.tsx), and
        // the Overview is home for everyone in the rollout, the same place `/payroll` sends them.
        submenu: [
            {
                name: 'Overview',
                path: '/payroll/overview',
                customValidation: payrollSetupCompleteValidation,
                featureGate: { name: FeatureGates.PayrollDashboardV2, value: true },
            },
            {
                name: 'Notifications',
                path: '/payroll/notifications',
                customValidation: payrollSetupCompleteValidation,
                featureGate: [
                    { name: FeatureGates.PayrollDashboardV2, value: true },
                    { name: FeatureGates.PayrollNotifications, value: true },
                ],
            },
            {
                name: 'Dashboard',
                path: '/payroll/dashboard',
                // Everyone outside the rollout keeps it. Inside, it stays only while
                // `payroll_dashboard_v2_show_legacy` is on.
                hideWhenGates: [
                    { name: FeatureGates.PayrollDashboardV2, value: true },
                    { name: FeatureGates.PayrollDashboardV2ShowLegacy, value: false },
                ],
            },
            {
                name: 'My compensation',
                path: '/payroll/compensation',
                customValidation: payrollSetupCompleteValidation,
                featureGate: { name: FeatureGates.PayrollDashboardV2, value: true },
            },
            {
                name: 'My team',
                path: '/payroll/team',
                customValidation: payrollSetupCompleteValidation,
                featureGate: { name: FeatureGates.PayrollDashboardV2, value: true },
            },
            {
                name: 'Payroll documents',
                path: '/payroll/documents',
                customValidation: payrollSetupCompleteValidation,
                featureGate: { name: FeatureGates.PayrollDashboardV2, value: true },
            },
            {
                name: 'Time off',
                path: '/payroll/time-off',
                customValidation: payrollSetupCompleteValidation,
                featureGate: { name: FeatureGates.PayrollDashboardV2, value: true },
            },
        ],
    },
    {
        name: 'Insurance',
        icon: ShieldCheckIcon,
        basePath: '/insurance',
        submenu: [
            {
                name: 'Business',
                path: '/insurance',
            },
            {
                name: 'Health',
                path: '/insurance/health',
                dynamicConfigGate: { config: DynamicConfigs.HealthInsuranceConfig, key: 'enabled', value: true },
                requiredTask: HEALTH_INSURANCE_UNLOCK_TASKS,
                lockedTooltip: (member) => getHealthInsuranceLockedTooltip(member?.usertype),
            },
        ],
    },
    {
        name: 'Taxes',
        icon: TimeCoinsIcon,
        basePath: '/tax',
        submenu: [
            {
                name: 'Quarterly payments',
                path: '/tax/quarterly',
            },
            {
                name: 'Business Tax Return',
                path: '/tax/business',
                customValidation: businessTaxReturnValidation,
            },
            {
                name: 'Individual Tax Return',
                path: '/tax/individual',
            },
        ],
    },
    {
        name: 'Documents',
        icon: NavigationDocumentsIcon,
        path: '/documents',
    },
    {
        name: 'Refer & Earn',
        icon: GiftIconThin,
        path: '/referrals',
        badge: { label: '$1000', variant: 'soft' },
        onClickCallback: () => {
            collectiveTracking.track({
                name: 'My Referral Clicked',
            });
        },
    },
];
